// Copyright 2025, University of Maryland, All Rights Reserved
import { axiosInstance, handleApiError } from './client'

export interface DiscoverableWorkspace {
  orgId: string
  wsId: string
  name: string
  description?: string
}

export interface WorkspaceSuggestion {
  orgId: string
  wsId: string
  name: string
  description?: string
  reason: string
}

export interface SuggestJobResponse {
  job_id: string
  status: string
  message: string
}

export interface WorkspaceRequestPayload {
  org_id: string
  request_type: 'join' | 'create'
  ws_id?: string
  ws_name?: string
  ws_description?: string
  message?: string
}

export interface WorkspaceQuestionPayload {
  org_id: string
  ws_id: string
  ws_name?: string
  question: string
}

export interface WorkspaceRequestResponse {
  request_id: string
  user_id: string
  display_name?: string
  department?: string
  org_id: string
  request_type: string
  ws_id?: string
  ws_name?: string
  ws_description?: string
  message?: string
  primary_title?: string
  status: string
  created_at: string
  resolved_at?: string
  resolved_by?: string
}

export const discoverApi = {
  /** Returns all workspaces across every org the user is not already a member of.
   *  Works for brand-new users with no org membership. */
  async getDiscoverableWorkspaces(): Promise<DiscoverableWorkspace[]> {
    try {
      const response = await axiosInstance.get<DiscoverableWorkspace[]>('/workspaces/discover')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async submitRequest(payload: WorkspaceRequestPayload): Promise<WorkspaceRequestResponse> {
    try {
      const response = await axiosInstance.post<WorkspaceRequestResponse>(
        '/workspace-request',
        payload
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async getMyRequests(): Promise<WorkspaceRequestResponse[]> {
    try {
      const response = await axiosInstance.get<WorkspaceRequestResponse[]>('/workspace-request/mine')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async listRequests(status?: string): Promise<WorkspaceRequestResponse[]> {
    try {
      const response = await axiosInstance.get<WorkspaceRequestResponse[]>(
        '/workspace-request',
        { params: status ? { status } : undefined }
      )
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async resolveRequest(
    requestId: string,
    action: 'approve' | 'reject',
    orgId?: string,
    wsName?: string,
    wsDescription?: string,
  ): Promise<{ request_id: string; status: string }> {
    try {
      const response = await axiosInstance.patch(`/workspace-request/${requestId}`, {
        action,
        ...(orgId ? { org_id: orgId } : {}),
        ...(wsName ? { ws_name: wsName } : {}),
        ...(wsDescription !== undefined ? { ws_description: wsDescription } : {}),
      })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async suggestWorkspaces(query: string): Promise<SuggestJobResponse> {
    try {
      const response = await axiosInstance.post<SuggestJobResponse>('/workspaces/suggest', { query })
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async fetchSuggestResult(jobId: string): Promise<WorkspaceSuggestion[]> {
    try {
      const response = await axiosInstance.get<WorkspaceSuggestion[]>(`/workspaces/suggest/${jobId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async askQuestion(payload: WorkspaceQuestionPayload): Promise<{ status: string }> {
    try {
      const response = await axiosInstance.post<{ status: string }>('/workspace-request/ask', payload)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },
}
